import React, {useState} from 'react';
import { Grid, Box, Typography } from "@mui/material";
import ProgressScale from 'components/ProgressScale';
import BasicSelect from 'components/select';
import FormDialog from 'components/modal';
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import LandlordPaymentsTable from 'components/Tables/LandlordPaymentsTable';
import DummyTable from 'components/DummyViews/DummyTable';
import { useGetLandlordPaymentsData, useGetLandlordPayments, useRentals, useGetMonthsData } from 'services/hooks/rentals';
import PlainCard from 'components/PlainCard';
import { withoutSymbolFormatter } from 'utilities/currencyFormatter';
import moment from "moment";

export default function LandlordPayments() {
    const [rental, setRental] = React.useState('all')
    const [monthPaidFor, setMonthPaidFor] = React.useState('all')
    const [open, setOpen] = React.useState(false);
    const [year, setYear] = React.useState(new Date().getFullYear());
    const [month, setMonth] = React.useState(moment().format("MM")); // Use "MM" format for numbers
    const {isLoadingPaymentsData, setLoadingPaymentsData} = useState(true)
    const { rentals } = useRentals()
	const {landlordPaymentsData: allTableData, isLoadingLandlordPaymentsData, errorLoadingLandlordPaymentsData} = useGetLandlordPaymentsData()
	const {landlordPaymentsData, isLoadingLandlordPaymentsData: isLoadingCardsData} = useGetLandlordPayments({ year, month })

	// Months array for filter - formatted to match BasicSelect expected structure
	const months = [
		{ id: "all", rental_name: "All Months" },
		{ id: "January", rental_name: "January" },
		{ id: "February", rental_name: "February" },
		{ id: "March", rental_name: "March" },
		{ id: "April", rental_name: "April" },
		{ id: "May", rental_name: "May" },
		{ id: "June", rental_name: "June" },
		{ id: "July", rental_name: "July" },
		{ id: "August", rental_name: "August" },
		{ id: "September", rental_name: "September" },
		{ id: "October", rental_name: "October" },
		{ id: "November", rental_name: "November" },
		{ id: "December", rental_name: "December" },
	];

	// Add "All Rentals" option to rentals array
	const rentalsWithAll = React.useMemo(() => {
		return [{ id: "all", rental_name: "All Rentals" }, ...(rentals || [])];
	}, [rentals]);

	// Filter table data based on selected rental and month paid for
	const filteredTableData = React.useMemo(() => {
		if (!allTableData || !Array.isArray(allTableData)) return [];
		
		return allTableData.filter((row) => {
			// Filter by rental - if rental is empty string or "all", show all
			const matchesRental = !rental || rental === "" || rental === "all" || row.related_landlord_rental?.id === rental;
			
			// Filter by month paid for - if monthPaidFor is empty string or "all", show all
			const matchesMonth = !monthPaidFor || monthPaidFor === "" || monthPaidFor === "all" || row.related_month?.month === monthPaidFor;
			
			return matchesRental && matchesMonth;
		});
	}, [allTableData, rental, monthPaidFor]);

	// Calculate card values from landlordPaymentsData
	const totalAmountCollected = landlordPaymentsData?.total_rental_payments || 0;
	const expectedCollection = landlordPaymentsData?.expected_collection || 0;
	const totalLandlordPayment = landlordPaymentsData?.total_landlord_payments || 0;
	const payPercentage = landlordPaymentsData?.pay_percentage || 0;
	const cummulativeLandlordBalance = landlordPaymentsData?.cumulative_landlord_balance || 0;

	// Calculate the amount to deduct based on the pay percentage
    const deductionAmount = totalAmountCollected * (payPercentage / 100);
    const adjustedTotalAmountCollected = totalAmountCollected - deductionAmount; // Adjusted amount

	// Format values with thousand separators and no decimals
	const formattedExpectedCollection = withoutSymbolFormatter(Number(expectedCollection));
	const formattedAdjustedTotalAmountCollected = withoutSymbolFormatter(Math.round(adjustedTotalAmountCollected));
	const formattedCummulativeLandlordBalance = withoutSymbolFormatter(Math.round(cummulativeLandlordBalance));
	const formattedTotalLandlordPayment = withoutSymbolFormatter(Math.round(totalLandlordPayment));
	
	// Calculate commission note - format to 2 decimal places
    const formattedPayPercentage = Number(payPercentage).toFixed(2);
    const note = `Excluding ${formattedPayPercentage}% Commission `;

    const handleRentalChange = (event) => {
        setRental(event.target.value);
    };

    const handleMonthPaidForChange = (event) => {
        setMonthPaidFor(event.target.value);
    };

    const handleClose = () => {
        setOpen(false);
    };
 

    return (
			<Box m="20px">
				<Grid container spacing={2}>
					<Grid item xs={12}>
						{(isLoadingPaymentsData || isLoadingCardsData) && <ProgressScale />}
						<h1>Landlord Payouts</h1>
						<Grid container>
							<Grid item xs={12} md={3}>
								<PlainCard name="Expected Collection" value={formattedExpectedCollection} note={note} />
							</Grid>
							<Grid item xs={12} md={3}>
								<PlainCard name="Total Amount Collected" value={formattedAdjustedTotalAmountCollected} note={note} />
							</Grid>
							<Grid item xs={12} md={3}>
								<PlainCard name="Total Amount Due" value={formattedCummulativeLandlordBalance}  />
							</Grid>
							<Grid item xs={12} md={3}>
								<PlainCard name="Total Paid to Landlord" value={formattedTotalLandlordPayment} />
							</Grid>
						</Grid>
						<Grid container spacing={2} sx={{ marginTop: 3 }}>
							<Grid item sx={{ display: { xs: "none", sm: "block" } }} sm={7} md={4}>
								<BasicSelect value={rental} handleChange={handleRentalChange} label={"Filter By Rental"} data={rentalsWithAll} />
							</Grid>
							<Grid item sx={{ display: { xs: "none", sm: "block" } }} sm={7} md={4}>
								<BasicSelect value={monthPaidFor} handleChange={handleMonthPaidForChange} label={"Filter By Month Paid For"} data={months} />
							</Grid>
							<Grid item sx={{ display: { xs: "none", sm: "block" } }} sm={7} md={4}>
								<Button
								className="add-button"
								variant="contained"
								endIcon={<AddIcon />}
								>
									Request Payout
								</Button>
							</Grid>
							<Grid item xs={12} sm={5} md={8} sx={{ display: { xs: "none", sm: "flex" }, justifyContent: "end", alignItems: "center" }}>
								
							</Grid>
							<Grid item sx={{ display: { xs: "block", sm: "none" } }} xs={12} sm={4}>
								<BasicSelect value={rental} handleChange={handleRentalChange} label={"Filter By Rental"} data={rentalsWithAll} />
							</Grid>
							<Grid item sx={{ display: { xs: "block", sm: "none" } }} xs={12} sm={4}>
								<BasicSelect value={monthPaidFor} handleChange={handleMonthPaidForChange} label={"Filter By Month Paid For"} data={months} />
							</Grid>
						</Grid>
						<Grid container spacing={2}>
							<Grid item xs={12} sx={{ marginTop: 3 }}>
								{isLoadingLandlordPaymentsData ? 
                                    <DummyTable /> : 
                                    <LandlordPaymentsTable rows={filteredTableData} />
                                }
							</Grid>
						</Grid>
						<FormDialog open={open} handleClose={handleClose} />
					</Grid>
				</Grid>
			</Box>
		);
}